import { useState, useEffect, type ChangeEvent, type ReactNode } from "react";
import Modal from "../../../../../../components/base/Modal";
import type { ThemeClasses } from "../types";
import type { UseFormReturn } from "react-hook-form";
import { Controller, useWatch } from "react-hook-form";
import Select from "react-select";
import { useQuery } from "@tanstack/react-query";
import { getCustomSelectStyles } from "../../../../shared";
import { fetchSgAccountConditions } from "../../../../../../features/sg/usuarios";

export const ORIGIN_OPTIONS = [
  { value: "v5", label: "V5" },
  { value: "mapon", label: "MAPON" },
  { value: "wialon", label: "WIALON" },
] as const;

export const ACCOUNT_CONDITION_OPTIONS = [
  { value: "", label: "Sin etiqueta" },
  { value: "Demo", label: "Demo" },
  { value: "Pruebas", label: "Pruebas" },
  { value: "Baja temporal", label: "Baja temporal" },
] as const;

export interface CompanyFormData {
  name: string;
  business_name: string;
  carrier_id: string;
  email: string;
  phone: string;
  rfc: string;
  status_id: string;
  origin: string[];
  account_condition: string;
  main_user: {
    user_name: string;
    first_name: string;
    last_name: string;
    email: string;
    username: string;
    password: string;
    map_interval: number | string;
    not_reporting_alert: boolean;
  };
  website: string;
  contact_person: {
    name: string;
    phone: string;
    email: string;
  };
  contact_address: {
    address1: string;
    address2: string;
    city: string;
    state: string;
    country: string;
    zip_code: string;
  };
  billing_address: {
    address1: string;
    address2: string;
    city: string;
    state: string;
    country: string;
    zip_code: string;
  };
  devices: string[];
}

export interface Device {
  id: string;
  identifier: string;
  model?: string;
  type?: string;
  status?: string;
}

interface CompanyAddModalProps {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  // El tercer genérico (TTransformedValues) que infiere useForm puede no
  // resolverse a CompanyFormData; lo dejamos abierto para aceptar el form tal cual.
  form: UseFormReturn<CompanyFormData, any, any>;
  loadingSubmit: boolean;
  onClose: () => void;
  onSubmit: (data: CompanyFormData) => void;
  onImageChange: (e: ChangeEvent<HTMLInputElement>) => void;
  imagePreview: string | null;
  isEdit?: boolean;
  hasFormChanges?: () => boolean;
  editCompanyId?: string | null;
}

function Section({
  title,
  open,
  onToggle,
  themeClasses,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  themeClasses: ThemeClasses;
  children: ReactNode;
}) {
  return (
    <div className={`border ${themeClasses.cardBorder} rounded-lg mt-3`}>
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-4 py-3 text-sm font-semibold ${themeClasses.text}`}
      >
        <span>{title}</span>
        <i
          className={`ri-arrow-down-s-line text-lg transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

export function CompanyAddModal({
  themeClasses,
  isDarkMode,
  form,
  loadingSubmit,
  onClose,
  onSubmit,
  onImageChange,
  imagePreview,
  isEdit = false,
  hasFormChanges,
  editCompanyId,
}: CompanyAddModalProps) {
  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = form;

  const watchedStatusId = useWatch({ control, name: "status_id" });
  const isSuspended = watchedStatusId === "Y2aD7bE1fF6jM3kO9hP5";

  const { data: sgConditions } = useQuery({
    queryKey: ["sg-account-conditions"],
    queryFn: fetchSgAccountConditions,
    staleTime: 5 * 60 * 1000,
  });

  const accountConditionOptions =
    sgConditions && sgConditions.length > 0
      ? [
          { value: "", label: "Sin etiqueta" },
          ...sgConditions.map((c: any) => ({ value: c.name, label: c.name })),
        ]
      : ACCOUNT_CONDITION_OPTIONS;

  const [open, setOpen] = useState({
    general: true,
    mainUser: false,
    contactPerson: false,
    contactAddress: false,
    billing: false,
  });
  const toggle = (key: keyof typeof open) =>
    setOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  const [showPassword, setShowPassword] = useState(false);

  // Abre automáticamente la sección que tenga errores de validación al enviar.
  useEffect(() => {
    setOpen((prev) => ({
      ...prev,
      general:
        prev.general || !!(errors.rfc || errors.origin || errors.status_id),
      contactPerson: prev.contactPerson || !!errors.contact_person,
      contactAddress: prev.contactAddress || !!errors.contact_address,
    }));
  }, [errors.rfc, errors.origin, errors.status_id, errors.contact_person, errors.contact_address]);

  useEffect(() => {
    if (!isSuspended) setValue("account_condition", "");
  }, [isSuspended, setValue]);

  const inputClassName = `w-full px-3 py-2 ${
    isDarkMode ? "bg-dark-card" : "bg-gray-50"
  } border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 outline-none transition-all`;

  const selectClassName = inputClassName;

  const labelClassName = `text-sm font-medium ${themeClasses.text} mb-1`;

  const requiredLabel = (label: string) => (
    <>
      {label}
      <span className="text-red-500 ml-1">*</span>
    </>
  );

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error("Error al copiar:", err);
    }
  };

  return (
    <Modal
      title={isEdit ? "Editar Compañía" : "Nueva Compañía"}
      onClose={onClose}
      themeClasses={themeClasses}
      width="max-w-4xl"
      content={
        <form onSubmit={form.handleSubmit(onSubmit)}>
          {loadingSubmit && (
            <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div
                className={`flex flex-col items-center gap-4 rounded-2xl px-10 py-8 shadow-2xl border ${themeClasses.bg} ${themeClasses.border}`}
              >
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary-500 border-t-transparent"></div>
                <p className={`text-sm font-medium ${themeClasses.text}`}>
                  {isEdit ? "Guardando cambios…" : "Creando compañía…"}
                </p>
              </div>
            </div>
          )}
          <div
            className={`mb-4 flex items-start gap-3 rounded-xl border px-4 py-3 ${
              isDarkMode
                ? "border-amber-500/30 bg-amber-500/10 text-amber-200"
                : "border-amber-300 bg-amber-50 text-amber-800"
            }`}
          >
            <i className="ri-links-line mt-0.5 text-lg"></i>
            <div>
              <p className="text-sm font-semibold">
                Sincronización V5 (Dumax) ↔ SG
              </p>
              <p className="mt-0.5 text-xs">
                Si el origen incluye V5, la cuenta se crea en V5 (Dumax) y en SG a
                la vez. Al editar los datos de una, el cambio se sincroniza
                automáticamente entre V5 y SG.
              </p>
            </div>
          </div>
          <div className="max-h-[60vh] overflow-y-auto overflow-x-hidden pr-1">
          {/* Logo + esenciales (siempre visibles) */}
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <div className="flex flex-col shrink-0">
              <label className={labelClassName}>Logo</label>
              <div className="relative w-32 h-32">
                {imagePreview ? (
                  <div className="relative w-full h-full">
                    <img
                      src={imagePreview}
                      alt="Logo preview"
                      className="w-full h-full object-contain rounded-lg border border-gray-300"
                    />
                    <label
                      htmlFor="company-logo-input"
                      className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 hover:opacity-100 transition-opacity cursor-pointer rounded-lg"
                    >
                      <span className="text-white font-bold text-sm">
                        Cambiar
                      </span>
                      <input
                        id="company-logo-input"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={onImageChange}
                      />
                    </label>
                  </div>
                ) : (
                  <label
                    htmlFor="company-logo-input"
                    className={`flex flex-col gap-1 items-center justify-center w-full h-full cursor-pointer border-dashed border-2 ${
                      isDarkMode ? "border-gray-600" : "border-gray-300"
                    } rounded-lg`}
                  >
                    <i
                      className={`ri-image-add-line text-3xl ${themeClasses.textSecondary}`}
                    />
                    <span
                      className={`text-xs text-center ${themeClasses.textSecondary}`}
                    >
                      Subir logo
                    </span>
                    <input
                      id="company-logo-input"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={onImageChange}
                    />
                  </label>
                )}
              </div>
            </div>

            <div className="flex-1 w-full grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex flex-col">
                <label className={labelClassName}>{requiredLabel("Nombre")}</label>
                <input
                  {...register("name", { required: "El nombre es requerido" })}
                  className={`${inputClassName} ${errors.name ? "border-red-500" : ""}`}
                  placeholder="Nombre de la compañía"
                />
                {errors.name && (
                  <span className="text-red-500 text-xs mt-1">
                    {errors.name.message}
                  </span>
                )}
              </div>

              <div className="flex flex-col">
                <label className={labelClassName}>{requiredLabel("Email")}</label>
                <input
                  type="email"
                  {...register("email", {
                    required: "El email es requerido",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Email inválido",
                    },
                  })}
                  className={`${inputClassName} ${errors.email ? "border-red-500" : ""}`}
                  placeholder="correo@empresa.com"
                />
                {errors.email && (
                  <span className="text-red-500 text-xs mt-1">
                    {errors.email.message}
                  </span>
                )}
              </div>

              <div className="flex flex-col">
                <label className={labelClassName}>
                  {requiredLabel("Razón Social")}
                </label>
                <input
                  {...register("business_name", {
                    required: "La razón social es requerida",
                  })}
                  className={`${inputClassName} ${errors.business_name ? "border-red-500" : ""}`}
                  placeholder="Razón social"
                />
                {errors.business_name && (
                  <span className="text-red-500 text-xs mt-1">
                    {errors.business_name.message}
                  </span>
                )}
              </div>

              <div className="flex flex-col">
                <label className={labelClassName}>
                  {requiredLabel("Teléfono")}
                </label>
                <input
                  type="tel"
                  {...register("phone", { required: "El teléfono es requerido" })}
                  className={`${inputClassName} ${errors.phone ? "border-red-500" : ""}`}
                  placeholder="+52 123 456 7890"
                />
                {errors.phone && (
                  <span className="text-red-500 text-xs mt-1">
                    {errors.phone.message}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Información General (colapsable) */}
          <Section
            title="Información General"
            open={open.general}
            onToggle={() => toggle("general")}
            themeClasses={themeClasses}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {isEdit && editCompanyId && (
                <div className="flex flex-col">
                  <label className={labelClassName}>Carrier ID</label>
                  <div className="flex items-center gap-2">
                    <input
                      {...register("carrier_id")}
                      className={`${inputClassName} bg-primary-500/20 border-primary-500/30 cursor-not-allowed`}
                      placeholder="ID personalizado"
                      disabled
                      readOnly
                    />
                    <button
                      type="button"
                      onClick={() => copyToClipboard(editCompanyId)}
                      className={`p-2 rounded ${themeClasses.buttonHover} ${themeClasses.buttonText}`}
                      title="Copiar Carrier ID"
                    >
                      <i className="ri-file-copy-line text-lg" />
                    </button>
                  </div>
                  <span className={`text-xs ${themeClasses.textSecondary} mt-1`}>
                    ID único de la compañía (no editable)
                  </span>
                </div>
              )}

              <div className="flex flex-col">
                <label className={labelClassName}>{requiredLabel("RFC")}</label>
                <input
                  {...register("rfc", { required: "El RFC es requerido" })}
                  className={`${inputClassName} ${errors.rfc ? "border-red-500" : ""}`}
                  placeholder="RFC de la empresa"
                />
                {errors.rfc && (
                  <span className="text-red-500 text-xs mt-1">
                    {errors.rfc.message}
                  </span>
                )}
              </div>

              <div className="flex flex-col">
                <label className={labelClassName}>Sitio Web</label>
                <input
                  type="url"
                  {...register("website")}
                  className={inputClassName}
                  placeholder="https://www.empresa.com"
                />
              </div>

              <div className="flex flex-col">
                <label className={labelClassName}>{requiredLabel("Origen")}</label>
                <Controller
                  name="origin"
                  control={control}
                  rules={{
                    validate: (v) =>
                      (Array.isArray(v) && v.length > 0) ||
                      "El origen es requerido",
                  }}
                  render={({ field }) => (
                    <Select
                      isMulti
                      options={ORIGIN_OPTIONS as unknown as { value: string; label: string }[]}
                      styles={getCustomSelectStyles(isDarkMode, !!errors.origin)}
                      placeholder="Seleccionar origen"
                      value={ORIGIN_OPTIONS.filter((o) =>
                        (field.value || []).includes(o.value),
                      )}
                      onChange={(opts: any) =>
                        field.onChange((opts || []).map((o: any) => o.value))
                      }
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  )}
                />
                {errors.origin && (
                  <span className="text-red-500 text-xs mt-1">
                    {errors.origin.message}
                  </span>
                )}
              </div>

              <div className="flex flex-col">
                <label className={labelClassName}>{requiredLabel("Estado")}</label>
                <select
                  {...register("status_id", {
                    required: "El estado es requerido",
                  })}
                  className={`${selectClassName} ${errors.status_id ? "border-red-500" : ""}`}
                >
                  <option value="">Seleccionar estado</option>
                  <option value="jXmjzHcBD5E1J0wjwHzq">Activa</option>
                  <option value="Y2aD7bE1fF6jM3kO9hP5">Suspendida</option>
                </select>
                {errors.status_id && (
                  <span className="text-red-500 text-xs mt-1">
                    {errors.status_id.message}
                  </span>
                )}
              </div>

              {isSuspended && (
                <div className="flex flex-col">
                  <label className={labelClassName}>Etiqueta</label>
                  <Controller
                    name="account_condition"
                    control={control}
                    render={({ field }) => (
                      <Select
                        options={
                          accountConditionOptions as unknown as {
                            value: string;
                            label: string;
                          }[]
                        }
                        styles={getCustomSelectStyles(isDarkMode, false)}
                        placeholder="Sin etiqueta"
                        isClearable
                        value={
                          accountConditionOptions.find(
                            (o) => o.value === (field.value || ""),
                          ) || null
                        }
                        onChange={(opt: any) =>
                          field.onChange(opt ? opt.value : "")
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    )}
                  />
                  <span className={`${labelClassName} text-xs mt-1 opacity-70`}>
                    Demo/Pruebas no se sincronizan con SG.
                  </span>
                </div>
              )}
            </div>
          </Section>

          {/* Usuario principal (SG) — colapsable */}
          <Section
            title="Usuario principal (SG)"
            open={open.mainUser}
            onToggle={() => toggle("mainUser")}
            themeClasses={themeClasses}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label className={labelClassName}>Usuario de acceso</label>
                <input
                  className={inputClassName}
                  placeholder="usuario"
                  {...register("main_user.user_name")}
                />
              </div>
              <div className="flex flex-col">
                <label className={labelClassName}>Nombre</label>
                <input
                  className={inputClassName}
                  {...register("main_user.first_name")}
                />
              </div>
              <div className="flex flex-col">
                <label className={labelClassName}>Apellidos</label>
                <input
                  className={inputClassName}
                  {...register("main_user.last_name")}
                />
              </div>
              <div className="flex flex-col">
                <label className={labelClassName}>Intervalo de mapa</label>
                <input
                  type="number"
                  className={inputClassName}
                  {...register("main_user.map_interval")}
                />
              </div>
              <div className="flex items-center gap-2 mt-6">
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-primary-500"
                  {...register("main_user.not_reporting_alert")}
                />
                <label className={themeClasses.text}>Alerta de no reporte</label>
              </div>
            </div>

            <div className={`mt-4 pt-4 border-t ${themeClasses.cardBorder}`}>
              <p className={`text-sm font-medium ${themeClasses.text}`}>
                Acceso a V5 (Dumax)
              </p>
              <p className={`text-xs mb-3 ${themeClasses.textSecondary}`}>
                {isEdit
                  ? "Usuario administrador de la compañía en V5 (login). La contraseña sólo se usa si ese usuario todavía no existe (se crea); la de un usuario existente no se cambia desde aquí: usa Cobranza → Usuarios → Cambiar contraseña, o que él use «¿Olvidaste tu contraseña?»."
                  : "Con estos datos se crea el usuario administrador de la compañía en V5 (login). Si el correo o el usuario ya existen, no se duplica."}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className={labelClassName}>Email de acceso</label>
                  <input
                    type="email"
                    className={inputClassName}
                    placeholder="correo@dominio.com"
                    {...register("main_user.email")}
                  />
                </div>
                <div className="flex flex-col">
                  <label className={labelClassName}>
                    Usuario (login V5)
                  </label>
                  <input
                    className={inputClassName}
                    placeholder="usuario_v5"
                    {...register("main_user.username")}
                  />
                </div>
                <div className="flex flex-col">
                  <label className={labelClassName}>Contraseña</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      className={`${inputClassName} pr-10`}
                      placeholder={
                        isEdit
                          ? "Sólo para crear el admin si aún no existe"
                          : "mín. 8 caracteres"
                      }
                      autoComplete="new-password"
                      {...register("main_user.password")}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                      aria-label={showPassword ? "Ocultar contraseña" : "Ver contraseña"}
                    >
                      <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Section>

          {/* Persona de Contacto — colapsable */}
          <Section
            title="Persona de Contacto"
            open={open.contactPerson}
            onToggle={() => toggle("contactPerson")}
            themeClasses={themeClasses}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex flex-col">
                <label className={labelClassName}>{requiredLabel("Nombre")}</label>
                <input
                  {...register("contact_person.name", {
                    required: "El nombre del contacto es requerido",
                  })}
                  className={`${inputClassName} ${errors.contact_person?.name ? "border-red-500" : ""}`}
                  placeholder="Nombre del contacto"
                />
                {errors.contact_person?.name && (
                  <span className="text-red-500 text-xs mt-1">
                    {errors.contact_person.name.message}
                  </span>
                )}
              </div>
              <div className="flex flex-col">
                <label className={labelClassName}>Teléfono</label>
                <input
                  type="tel"
                  {...register("contact_person.phone")}
                  className={inputClassName}
                  placeholder="+52 123 456 7890"
                />
              </div>
              <div className="flex flex-col">
                <label className={labelClassName}>Email</label>
                <input
                  type="email"
                  {...register("contact_person.email")}
                  className={inputClassName}
                  placeholder="contacto@empresa.com"
                />
              </div>
            </div>
          </Section>

          {/* Dirección de Contacto — colapsable */}
          <Section
            title="Dirección de Contacto"
            open={open.contactAddress}
            onToggle={() => toggle("contactAddress")}
            themeClasses={themeClasses}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex flex-col lg:col-span-2">
                <label className={labelClassName}>
                  {requiredLabel("Dirección 1")}
                </label>
                <input
                  {...register("contact_address.address1", {
                    required: "La dirección es requerida",
                  })}
                  className={`${inputClassName} ${errors.contact_address?.address1 ? "border-red-500" : ""}`}
                  placeholder="Calle y número"
                />
                {errors.contact_address?.address1 && (
                  <span className="text-red-500 text-xs mt-1">
                    {errors.contact_address.address1.message}
                  </span>
                )}
              </div>
              <div className="flex flex-col lg:col-span-2">
                <label className={labelClassName}>Dirección 2</label>
                <input
                  {...register("contact_address.address2")}
                  className={inputClassName}
                  placeholder="Colonia, edificio, etc."
                />
              </div>
              <div className="flex flex-col">
                <label className={labelClassName}>{requiredLabel("Ciudad")}</label>
                <input
                  {...register("contact_address.city", {
                    required: "La ciudad es requerida",
                  })}
                  className={`${inputClassName} ${errors.contact_address?.city ? "border-red-500" : ""}`}
                  placeholder="Ciudad"
                />
                {errors.contact_address?.city && (
                  <span className="text-red-500 text-xs mt-1">
                    {errors.contact_address.city.message}
                  </span>
                )}
              </div>
              <div className="flex flex-col">
                <label className={labelClassName}>
                  {requiredLabel("Estado/Provincia")}
                </label>
                <input
                  {...register("contact_address.state", {
                    required: "El estado es requerido",
                  })}
                  className={`${inputClassName} ${errors.contact_address?.state ? "border-red-500" : ""}`}
                  placeholder="Estado"
                />
                {errors.contact_address?.state && (
                  <span className="text-red-500 text-xs mt-1">
                    {errors.contact_address.state.message}
                  </span>
                )}
              </div>
              <div className="flex flex-col">
                <label className={labelClassName}>{requiredLabel("País")}</label>
                <select
                  {...register("contact_address.country", {
                    required: "El país es requerido",
                  })}
                  className={`${selectClassName} ${errors.contact_address?.country ? "border-red-500" : ""}`}
                >
                  <option value="">Seleccionar país</option>
                  <option value="MX">México</option>
                  <option value="US">Estados Unidos</option>
                  <option value="CA">Canadá</option>
                  <option value="GT">Guatemala</option>
                  <option value="HN">Honduras</option>
                  <option value="SV">El Salvador</option>
                  <option value="CO">Colombia</option>
                  <option value="AR">Argentina</option>
                  <option value="CL">Chile</option>
                  <option value="PE">Perú</option>
                </select>
                {errors.contact_address?.country && (
                  <span className="text-red-500 text-xs mt-1">
                    {errors.contact_address.country.message}
                  </span>
                )}
              </div>
              <div className="flex flex-col">
                <label className={labelClassName}>
                  {requiredLabel("Código Postal")}
                </label>
                <input
                  {...register("contact_address.zip_code", {
                    required: "El código postal es requerido",
                  })}
                  className={`${inputClassName} ${errors.contact_address?.zip_code ? "border-red-500" : ""}`}
                  placeholder="00000"
                />
                {errors.contact_address?.zip_code && (
                  <span className="text-red-500 text-xs mt-1">
                    {errors.contact_address.zip_code.message}
                  </span>
                )}
              </div>
            </div>
          </Section>

          {/* Dirección de Facturación — colapsable */}
          <Section
            title="Dirección de Facturación"
            open={open.billing}
            onToggle={() => toggle("billing")}
            themeClasses={themeClasses}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex flex-col lg:col-span-2">
                <label className={labelClassName}>Dirección 1</label>
                <input
                  {...register("billing_address.address1")}
                  className={inputClassName}
                  placeholder="Calle y número"
                />
              </div>
              <div className="flex flex-col lg:col-span-2">
                <label className={labelClassName}>Dirección 2</label>
                <input
                  {...register("billing_address.address2")}
                  className={inputClassName}
                  placeholder="Colonia, edificio, etc."
                />
              </div>
              <div className="flex flex-col">
                <label className={labelClassName}>Ciudad</label>
                <input
                  {...register("billing_address.city")}
                  className={inputClassName}
                  placeholder="Ciudad"
                />
              </div>
              <div className="flex flex-col">
                <label className={labelClassName}>Estado/Provincia</label>
                <input
                  {...register("billing_address.state")}
                  className={inputClassName}
                  placeholder="Estado"
                />
              </div>
              <div className="flex flex-col">
                <label className={labelClassName}>País</label>
                <select
                  {...register("billing_address.country")}
                  className={selectClassName}
                >
                  <option value="">Seleccionar país</option>
                  <option value="MX">México</option>
                  <option value="US">Estados Unidos</option>
                  <option value="CA">Canadá</option>
                  <option value="GT">Guatemala</option>
                  <option value="HN">Honduras</option>
                  <option value="SV">El Salvador</option>
                  <option value="CO">Colombia</option>
                  <option value="AR">Argentina</option>
                  <option value="CL">Chile</option>
                  <option value="PE">Perú</option>
                </select>
              </div>
              <div className="flex flex-col">
                <label className={labelClassName}>Código Postal</label>
                <input
                  {...register("billing_address.zip_code")}
                  className={inputClassName}
                  placeholder="00000"
                />
              </div>
            </div>
          </Section>

          </div>
          {/* Botones */}
          <div className="flex justify-end space-x-3 pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} font-medium transition-colors`}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={
                loadingSubmit ||
                (isEdit &&
                  typeof hasFormChanges === "function" &&
                  !hasFormChanges())
              }
              className="px-4 py-2 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingSubmit
                ? "Guardando..."
                : isEdit
                  ? "Actualizar Compañía"
                  : "Crear Compañía"}
            </button>
          </div>
        </form>
      }
    />
  );
}
